/* GRYD v2 "Poster" — every component and helper, as ES modules.
   The same surface as components/bundle.js, which stays the reference implementation:
   the components are split one per file, the values are unchanged.

   Prototypes that expect the global can still do:
     import GRYD2 from './src/index.js'; window.GRYD2 = GRYD2;
   Production code imports what it needs by name. */

import { fx } from './fx.js';
import { useFocusTrap, useHotkey, useTheme } from './hooks.js';
import { applyTheme } from './theme.js';
import { BODY, MONO, DISPLAY, TITLE, ink, inputS, inputCompactS, secretInputProps, secretMaskS, cardS } from './primitives.js';
import { agentColor, agentLabel, agentAvatar, agentId, agentPortraits, brandMark } from './agents.js';
import { smoothPath } from './charts.js';
import { Btn } from './components/Btn/Btn.jsx';
import { Seg } from './components/Seg/Seg.jsx';
import { Tag, TONES } from './components/Tag/Tag.jsx';
import { Stamp } from './components/Stamp/Stamp.jsx';
import { Toggle } from './components/Toggle/Toggle.jsx';
import { SearchBox } from './components/SearchBox/SearchBox.jsx';
import { RevalidateDot } from './components/RevalidateDot/RevalidateDot.jsx';
import { ThemeToggle } from './components/ThemeToggle/ThemeToggle.jsx';
import { Field } from './components/Field/Field.jsx';
import { Card } from './components/Card/Card.jsx';
import { Inset } from './components/Inset/Inset.jsx';
import { H1 } from './components/H1/H1.jsx';
import { SectionLabel } from './components/SectionLabel/SectionLabel.jsx';
import { PageHeader } from './components/PageHeader/PageHeader.jsx';
import { ViewOnlyPill } from './components/ViewOnlyPill/ViewOnlyPill.jsx';
import { Stat } from './components/Stat/Stat.jsx';
import { EmptyState } from './components/EmptyState/EmptyState.jsx';
import { Modal } from './components/Modal/Modal.jsx';
import { Toast } from './components/Toast/Toast.jsx';
import { Skeleton } from './components/Skeleton/Skeleton.jsx';
import { DocRef } from './components/DocRef/DocRef.jsx';
import { AgentTile } from './components/AgentTile/AgentTile.jsx';
import { PersonAvatar } from './components/PersonAvatar/PersonAvatar.jsx';
import { MonoTile } from './components/MonoTile/MonoTile.jsx';
import { AccessRow } from './components/AccessRow/AccessRow.jsx';
import { Bar } from './components/Bar/Bar.jsx';
import { Dots } from './components/Dots/Dots.jsx';
import { Tok } from './components/Tok/Tok.jsx';
import { Odometer } from './components/Odometer/Odometer.jsx';
import { Scramble } from './components/Scramble/Scramble.jsx';
import { Marquee } from './components/Marquee/Marquee.jsx';
import { Icon } from './components/Icon/Icon.jsx';
import { IconBtn } from './components/IconBtn/IconBtn.jsx';
import { Input } from './components/Input/Input.jsx';
import { Select } from './components/Select/Select.jsx';
import { Checkbox } from './components/Checkbox/Checkbox.jsx';
import { CardHeader } from './components/CardHeader/CardHeader.jsx';
import { CardTitle } from './components/CardTitle/CardTitle.jsx';
import { CardActions } from './components/CardActions/CardActions.jsx';
import { CardContent } from './components/CardContent/CardContent.jsx';
import { CardFooter } from './components/CardFooter/CardFooter.jsx';
import { Table } from './components/Table/Table.jsx';
import { Sheet } from './components/Sheet/Sheet.jsx';
import { Menu } from './components/Menu/Menu.jsx';
import { Banner } from './components/Banner/Banner.jsx';
import { SaveState } from './components/SaveState/SaveState.jsx';
import { Breadcrumbs } from './components/Breadcrumbs/Breadcrumbs.jsx';
import { Trend } from './components/Trend/Trend.jsx';
import { Sparkline } from './components/Sparkline/Sparkline.jsx';
import { Meter } from './components/Meter/Meter.jsx';
import { Tooltip } from './components/Tooltip/Tooltip.jsx';
import { Textarea } from './components/Textarea/Textarea.jsx';
import { Toaster, toast } from './components/Toaster/Toaster.jsx';
import { Confirm } from './components/Confirm/Confirm.jsx';
import { Pagination } from './components/Pagination/Pagination.jsx';
import { Rail } from './components/Rail/Rail.jsx';
import { AppShell } from './components/AppShell/AppShell.jsx';
import { CommandPalette } from './components/CommandPalette/CommandPalette.jsx';
import { RunTimeline } from './components/RunTimeline/RunTimeline.jsx';
import { CodeBlock } from './components/CodeBlock/CodeBlock.jsx';
import { ChatMessage } from './components/ChatMessage/ChatMessage.jsx';
import { BarChart } from './components/BarChart/BarChart.jsx';
import { LineChart } from './components/LineChart/LineChart.jsx';
import { Ring } from './components/Ring/Ring.jsx';

export {
  fx, useFocusTrap, useHotkey, useTheme, applyTheme, BODY,
  MONO, DISPLAY, TITLE, ink, inputS, inputCompactS,
  secretInputProps, secretMaskS, cardS, agentColor, agentLabel, agentAvatar,
  agentId, agentPortraits, brandMark, smoothPath, Btn, Seg,
  Tag, TONES, Stamp, Toggle, SearchBox, RevalidateDot,
  ThemeToggle, Field, Card, Inset, H1, SectionLabel,
  PageHeader, ViewOnlyPill, Stat, EmptyState, Modal, Toast,
  Skeleton, DocRef, AgentTile, PersonAvatar, MonoTile, AccessRow,
  Bar, Dots, Tok, Odometer, Scramble, Marquee,
  Icon, IconBtn, Input, Select, Checkbox, CardHeader,
  CardTitle, CardActions, CardContent, CardFooter, Table, Sheet,
  Menu, Banner, SaveState, Breadcrumbs, Trend, Sparkline,
  Meter, Tooltip, Textarea, Toaster, toast, Confirm,
  Pagination, Rail, AppShell, CommandPalette, RunTimeline, CodeBlock,
  ChatMessage, BarChart, LineChart, Ring
};

export const version = '2.3.0';

/** Every export bundle.js puts on window.GRYD2, in one object, for prototypes that mount it
    as the global. The font and border primitives are module-only, as they are in the bundle. */
const GRYD2 = {
  fx, useFocusTrap, useHotkey, useTheme, applyTheme, inputS,
  inputCompactS, secretInputProps, secretMaskS, cardS, agentColor, agentLabel,
  agentAvatar, agentId, agentPortraits, brandMark, smoothPath, Btn,
  Seg, Tag, TONES, Stamp, Toggle, SearchBox,
  RevalidateDot, ThemeToggle, Field, Card, Inset, H1,
  SectionLabel, PageHeader, ViewOnlyPill, Stat, EmptyState, Modal,
  Toast, Skeleton, DocRef, AgentTile, PersonAvatar, MonoTile,
  AccessRow, Bar, Dots, Tok, Odometer, Scramble,
  Marquee, Icon, IconBtn, Input, Select, Checkbox,
  CardHeader, CardTitle, CardActions, CardContent, CardFooter, Table,
  Sheet, Menu, Banner, SaveState, Breadcrumbs, Trend,
  Sparkline, Meter, Tooltip, Textarea, Toaster, toast,
  Confirm, Pagination, Rail, AppShell, CommandPalette, RunTimeline,
  CodeBlock, ChatMessage, BarChart, LineChart, Ring, version
};

/** Resolves immediately; kept for code written against the template loader. */
export const ready = Promise.resolve(GRYD2);
GRYD2.ready = ready;

export default GRYD2;
